import { computed, inject } from 'vue'

import { linksProviderKey } from '../../../../constants'
import type { ContentItemLinkDetails } from '../../../../types'
import { isInternalLink } from '../../../../utils'
import type { ViewMode } from '../content-item-details.types'

type Campaign = NonNullable<ContentItemLinkDetails['campaigns']>[number]

export type UseContentItemLinksOptions = {
  data: () => ContentItemLinkDetails | null
  viewMode: () => ViewMode
}

export const useContentItemLinks = ({ data, viewMode }: UseContentItemLinksOptions) => {
  const contentItemOverviewLinks = inject(linksProviderKey)

  const creatorLink = computed(() => {
    const creator = data()?.creator
    if (!creator) {
      return null
    }
    if (viewMode() === 'admin') {
      return `/creators/${creator.id}/info`
    }
    return contentItemOverviewLinks?.getCreatorLink(creator.id) ?? null
  })

  const creatorLinkComponent = computed(() => {
    if (!creatorLink.value) {
      return null
    }
    if (viewMode() === 'admin') {
      return 'router-link'
    }
    return isInternalLink(creatorLink.value) ? 'router-link' : 'a'
  })

  const creatorLinkProps = computed(() => {
    if (!creatorLink.value) {
      return {}
    }
    return creatorLinkComponent.value === 'router-link'
      ? { to: creatorLink.value }
      : { href: creatorLink.value }
  })

  const advertiserLink = computed(() => {
    const advertiser = data()?.advertiser
    if (!advertiser) {
      return null
    }
    return contentItemOverviewLinks?.getAdvertiserLink(advertiser.id)
  })

  const affiliateLinkDetailsLink = computed(() => {
    const affiliateLink = data()?.affiliateLink
    const creator = data()?.creator
    if (!affiliateLink || !creator) {
      return null
    }
    if (viewMode() === 'admin') {
      return `/creators/${creator.id}/links?linkId=${affiliateLink.id}`
    }
    return affiliateLink.originalUrl ?? null
  })

  const getCampaignLink = (campaignId: string | number) =>
    contentItemOverviewLinks?.getCampaignLink(campaignId)
  const campaigns = computed(() => data()?.campaigns ?? [])

  const getCampaignDisplayName = (campaign: Campaign) => {
    if (viewMode() === 'advertiser') {
      return campaign.title || `Campaign #${campaign.id}`
    }
    return campaign.name || campaign.title || `Campaign #${campaign.id}`
  }

  const hasCampaigns = computed(() => campaigns.value.length > 0)
  const canAddCampaign = computed(() => viewMode() === 'admin' && Boolean(data()?.advertiser))
  const showNoCampaigns = computed(() => !hasCampaigns.value && !canAddCampaign.value)

  return {
    creatorLink,
    creatorLinkComponent,
    creatorLinkProps,
    advertiserLink,
    affiliateLinkDetailsLink,
    getCampaignLink,
    campaigns,
    getCampaignDisplayName,
    hasCampaigns,
    canAddCampaign,
    showNoCampaigns,
  }
}
